import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { FlyoutService } from '../flyout.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { Account } from '../../back-office/account.model';
import { ExplainerDialogService } from '../../core/explainer-dialog.service';
import {
  SubscriptionFrequency,
  Limitations,
  SubscriptionProvider,
  SubscriptionDiscount,
} from '../../subscription/subscription.model';
import { SubscriptionService } from '../../subscription/subscription.service';
import { applyDiscount, getDiscountTranslationKey, getPriceDigitsInfo } from '../../subscription/subscription-discount';
import { Language } from '../../core/identity/language';
import { IdentityService } from '../../core/identity/identity.service';
import { UserLanguage } from 'app/account-settings/models/user.model';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';
import { IMtr } from '../../core/tracker/mtr.interface';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { DateService } from 'app/shared/services/date-service';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { CurrentContext, CurrentContextService } from 'app/core/current-context.service';
import { Tier } from 'app/shared/account/tier.model';
import { ApplicationStarterService } from 'app/core/application-starter.service';
import { Currency } from '../../shared/account/Currency';

@Component({
  selector: 'app-flyout-upgrade-account',
  templateUrl: './flyout-upgrade-account.component.html',
  styleUrls: ['./flyout-upgrade-account.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutUpgradeAccountComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  private openingData: FlyoutOpeningData;
  private testPrice?: number;
  public currentAccount: Account;
  public saveInProgress = false;
  public checkoutInProgress = false;

  public currentCurrency = Currency.Eur;
  public currencyOptions = [Currency.Eur, Currency.Sek, Currency.Usd, Currency.Gbp];
  public isTrial: boolean;
  public taxLocationCountry: string;
  public Tier = Tier;
  public tier: Tier;
  public showAccountLimitMessage = false;
  public currentAccountsLevel = 1;
  public maxSubAccountsLevel = 4;
  public maxAccountsLevel = 30;
  public subscriptionExists: boolean;
  private calculationRequestSubject = new Subject();
  private subscriptionArray: Subscription[] = [];
  public currentBillingFrequency: SubscriptionFrequency;
  public inputDataIsUpdated = false;
  public disableSaveTooltip: string;
  public policyAccepted = false;
  public calculatedPrice: number;
  public monthlyPriceFromYear: number;
  public discount?: SubscriptionDiscount;
  public discountTranslationKey: string;
  public discountedPrice: number;
  public discountedPriceDigitsInfo: string;
  public discountedMonthlyPriceFromYear: number;
  public errorOnCalculation = false;
  public loadingCalculation = false;
  public showContactSales = false;
  public isSubscriptionActive = true;
  public errorOnSavingCalculation = false;

  public SubscriptionFrequency = SubscriptionFrequency;
  public SubscriptionProvider = SubscriptionProvider;
  public provider: SubscriptionProvider = SubscriptionProvider.Stripe;
  public EXPLAINER_TEXTS: any;

  public currentCustomer: CustomerModel;

  private initialTrafficLimit: number;
  private initialSurveyResponsesLimit: number;
  private initialAccountsLimit: number;

  public surveyResponsesLevelsMap: { [id: number]: number };
  public surveyResponsesLevelsCount: number;
  public currentSurveyResponsesLevel: number;
  private defaultSurveyResponses: number;
  private currentSurveyPerMonth: number;
  public showSurveyWarning = false;

  public trafficLevelsMap: { [id: number]: number };
  public trafficLevelsCount: number;
  public currentTrafficLevel: number;
  private currentTraffic: number;
  public showTrafficDowngradeWarning = false;
  private isUserVerified: boolean;

  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this.openingData = openingData;
    this.subscriptionExists = openingData.subscriptionExists;
    this.route.queryParams.subscribe((queryParam: any) => {
      if (queryParam['test-price']) {
        this.testPrice = queryParam['test-price'];
      }
    });
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this.openingData;
  }

  public get allowDowngrade(): boolean {
    return this.provider === SubscriptionProvider.Shopify;
  }

  // A Shopify plan change always mints a new appSubscriptionCreate, so "no changes to save" cannot
  // block it. The Stripe unlock keys off endedAt, which the Shopify read path never projects.
  private get requiresChangedInput(): boolean {
    return !this.isTrial && this.provider !== SubscriptionProvider.Shopify;
  }

  public get isSaveDisabled(): boolean {
    return (
      (this.requiresChangedInput && !this.inputDataIsUpdated) ||
      !this.policyAccepted ||
      this.showContactSales ||
      this.currentAccountsLevel > this.maxAccountsLevel ||
      !this.isUserVerified
    );
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly route: ActivatedRoute,
    private readonly flyoutService: FlyoutService,
    private readonly identityService: IdentityService,
    private readonly snackBarService: SnackBarService,
    private readonly subscriptionService: SubscriptionService,
    public explainerDialogService: ExplainerDialogService,
    private readonly analyticsService: AnalyticsService,
    private readonly dateService: DateService,
    @Inject('Mtr') private readonly mtr: IMtr,
    private readonly currentContextService: CurrentContextService,
    private readonly translateService: TranslateService,
  ) {
    this.logger = loggerFactory.get('FlyoutUpgradeAccountComponent');
  }

  ngOnDestroy(): void {
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  ngOnInit(): void {
    this.subscriptionArray.push(
      this.currentContextService.currentContextBS.subscribe((context: CurrentContext) => {
        if (context) {
          if (!this.openingData) {
            throw Error('"openingData" not found');
          }
          const customer = context.customer;
          if (!customer) {
            throw Error('No customer found');
          }
          const limitations = context.limitations;
          if (!limitations) {
            throw Error('No limitations found');
          }

          this.isUserVerified = this.identityService.currentIdentity.isVerified;
          this.currentAccount = context.account;
          this.currentCustomer = customer;
          this.provider = customer.subscriptionProvider ?? SubscriptionProvider.Stripe;
          if (this.provider === SubscriptionProvider.Shopify && context.account?.currency) {
            this.currentCurrency = context.account.currency;
          }
          this.tier = this.openingData?.tier || customer.tier;
          this.currentCustomer.tier = this.tier;

          this.subscriptionService.getTierLimitations(customer.tier).subscribe((tierLimitations) => {
            this.defaultSurveyResponses = tierLimitations.defaultSurveyResponses;
            this.EXPLAINER_TEXTS = this.getExplainerTexts();

            this.trafficLevelsMap = this.convertToLevelsMap(tierLimitations.events);
            this.trafficLevelsCount = tierLimitations.events.length;
            this.surveyResponsesLevelsMap = this.convertToLevelsMap(tierLimitations.surveyResponses);
            this.surveyResponsesLevelsCount = tierLimitations.surveyResponses.length;

            this.initialTrafficLimit = limitations.eventsPerMonth;
            this.initialAccountsLimit = limitations.accounts;
            this.initialSurveyResponsesLimit = limitations.surveyResponsesPerMonth;

            this.currentAccountsLevel = limitations.accounts;
            // A trial's limit is a config default, not a chosen plan, so start from the floor.
            this.currentTrafficLevel = this.subscriptionExists
              ? this.getCurrentLevelIndex(tierLimitations.events, limitations.eventsPerMonth)
              : 1;
            this.currentSurveyResponsesLevel = this.getCurrentLevelIndex(
              tierLimitations.surveyResponses,
              limitations.surveyResponsesPerMonth,
            );

            this.initializeWithOpeningData();
            this.initializeStatistics(customer, limitations);
          });
        }
      }),
    );

    this.subscriptionArray.push(
      this.calculationRequestSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
        this.recalculatePrice();
      }),
    );
  }

  private convertToLevelsMap(array: number[]): { [id: number]: number } {
    const result: { [id: number]: number } = {};
    for (let index = 0; index < array.length; index++) {
      result[index + 1] = array[index]; // Sliders need to have it starting at 1
    }
    return result;
  }

  private getCurrentLevelIndex(array: number[], value: number) {
    return array.findIndex((x) => x === value) + 1; // Sliders need to have it starting at 1
  }

  private initializeStatistics(customer: CustomerModel, limitations: Limitations): void {
    this.analyticsService
      .getCustomerStatistics(
        customer.id,
        this.subscriptionService.getStartDate(limitations.resetDayInMonth),
        this.dateService.nowLocal(),
      )
      .subscribe(
        (statistics) => {
          this.currentTraffic = statistics.pageviews;
          this.currentSurveyPerMonth = statistics.surveyResponses;
          // The floor a trial starts from can already be below what the store has used.
          if (!this.subscriptionExists) {
            this.showTrafficDowngradeWarning = this.trafficLevelsMap[this.currentTrafficLevel] < this.currentTraffic;
          }
        },
        () => {
          this.logger.error('Could not get customer statistics');
        },
      );
  }

  private getExplainerTexts(): any {
    return {
      traffic: {
        header: 'billing.traffic-title',
        description: 'billing.traffic-input',
      },
      subAccounts: {
        header: 'billing.accounts-title',
        description: 'billing.accounts-input',
      },
      accounts: {
        description: 'billing.accounts',
      },
      frequency: {
        header: 'billing.frequency-title',
        description: 'billing.frequency-input',
      },
      surveyResponses: {
        header: 'billing.surveys-title',
        descriptionKey: this.translateService.instant('explainer.billing.surveys-input', {
          defaultSurveyResponsesLimit: this.defaultSurveyResponses,
        }),
      },
    };
  }

  private initializeWithOpeningData(): void {
    this.isTrial = !this.openingData.subscriptionDetails;
    this.currentCurrency = this.openingData.subscriptionCurrency;
    if (this.openingData.subscriptionDetails) {
      this.currentBillingFrequency = this.openingData.subscriptionDetails.billingFrequency;
      this.taxLocationCountry = this.openingData.subscriptionDetails.taxLocationCountry;
      this.isSubscriptionActive = !this.openingData.subscriptionDetails.endedAt;
      if (!this.isSubscriptionActive) {
        this.inputDataIsUpdated = true;
      }
      this.discount = this.openingData.subscriptionDetails.discount;
      if (this.discount) {
        this.discountTranslationKey = getDiscountTranslationKey(this.discount);
      }
    } else {
      this.currentBillingFrequency = SubscriptionFrequency.Year;
    }
    this.recalculatePrice();
    this.checkAndUpdateIfSavePossible();
  }

  public togglePolicy(eventData: { data: { policyAccepted: boolean } }): void {
    this.policyAccepted = eventData.data.policyAccepted;
    this.checkAndUpdateIfSavePossible();
  }

  public trafficLevelChanged(e) {
    this.currentTrafficLevel = e.value;
    const currentTrafficLimit = this.trafficLevelsMap[this.currentTrafficLevel];
    if (currentTrafficLimit < this.currentTraffic) {
      this.showTrafficDowngradeWarning = true;
    } else {
      this.showTrafficDowngradeWarning = false;
    }
    this.sendRecalculationRequest();
  }

  public accountsLevelChanged(e) {
    setTimeout(() => {
      // Nothing reads the input's min validator, so this snap-back is the only floor.
      const lowestAllowedLevel = this.allowDowngrade ? 1 : this.initialAccountsLimit;
      if (e.value < lowestAllowedLevel) {
        this.showAccountLimitMessage = true;
        this.currentAccountsLevel = lowestAllowedLevel;
        if (this.allowDowngrade) {
          this.sendRecalculationRequest();
        }
        return;
      }
      this.showAccountLimitMessage = false;
      this.currentAccountsLevel = e.value;
      this.sendRecalculationRequest();
    }, 250);
  }

  public surveyResponsesLevelChanged(e) {
    this.currentSurveyResponsesLevel = e.value;
    if (this.getCurrentSurveyResponsesPerMonthOrDefaultLimit() < this.currentSurveyPerMonth) {
      this.showSurveyWarning = true;
    } else {
      this.showSurveyWarning = false;
    }
    this.sendRecalculationRequest();
  }

  public sendRecalculationRequest() {
    this.checkIfInputDataChanged();
    this.checkTrafficAndSetContactSalesFlag();
    this.calculationRequestSubject.next({});
  }

  public sendContactUsCanBeRequest() {
    this.logger.debug(`Should send a contact us request using goals...`);
    this.mtr.goal('Draged slider to contact us');
  }

  private checkTrafficAndSetContactSalesFlag() {
    const traffic = this.trafficLevelsMap[this.currentTrafficLevel];
    if (!traffic) {
      this.showContactSales = true;
      this.sendContactUsCanBeRequest();
      this.checkAndUpdateIfSavePossible();
    } else {
      this.showContactSales = false;
      this.checkAndUpdateIfSavePossible();
    }
    return traffic;
  }

  public recalculatePrice() {
    const traffic = this.checkTrafficAndSetContactSalesFlag();
    if (!traffic) {
      return;
    }
    this.errorOnCalculation = false;
    this.loadingCalculation = true;
    this.subscriptionService
      .getPriceCalculation({
        tier: this.tier,
        currency: this.currentCurrency,
        billingFrequency: this.currentBillingFrequency,
        eventsPerMonth: traffic,
        accounts: this.currentAccountsLevel,
        surveyResponsesPerMonth: this.surveyResponsesLevelsMap[this.currentSurveyResponsesLevel],
        testPrice: this.testPrice,
      })
      .subscribe(
        (result: number) => {
          this.loadingCalculation = false;
          this.errorOnCalculation = false;
          this.calculatedPrice = result;
          this.setMonthlyPriceFromYear();
          this.setDiscountedPrices();
        },
        () => {
          this.errorOnCalculation = true;
          this.loadingCalculation = false;
        },
      );
  }

  private checkIfInputDataChanged(): void {
    if (this.isTrial === true) {
      this.inputDataIsUpdated = false;
    } else {
      const currentTrafficLimit = this.trafficLevelsMap[this.currentTrafficLevel];
      const isTrafficUpdated = currentTrafficLimit !== this.initialTrafficLimit;
      const currentSurveyResponsesLimit = this.surveyResponsesLevelsMap[this.currentSurveyResponsesLevel];
      const isSurveyResponsesUpdated = currentSurveyResponsesLimit !== this.initialSurveyResponsesLimit;
      const isAccountsUpdated = this.initialAccountsLimit !== this.currentAccountsLevel;
      const isBillingFrequencyUpdated =
        this.openingData.subscriptionDetails.billingFrequency !== this.currentBillingFrequency;
      const isTierUpdated = this.currentCustomer.tier !== this.tier;
      const isTestPriceWithNoStripeChanges =
        this.testPrice != null &&
        Number(this.testPrice) === this.openingData.subscriptionDetails?.price &&
        !isBillingFrequencyUpdated;
      this.inputDataIsUpdated =
        !isTestPriceWithNoStripeChanges &&
        (isTrafficUpdated ||
          isSurveyResponsesUpdated ||
          isAccountsUpdated ||
          isBillingFrequencyUpdated ||
          isTierUpdated ||
          !this.isSubscriptionActive);
    }

    this.checkAndUpdateIfSavePossible();
  }

  public checkAndUpdateIfSavePossible(): void {
    if (this.showContactSales === true) {
      this.disableSaveTooltip = 'subscription.new-usage.no-save-contact-sales';
    } else if (!this.isUserVerified) {
      this.disableSaveTooltip = 'registration.userIsNotVerified';
    } else if (!this.policyAccepted) {
      this.disableSaveTooltip = 'subscription.new-usage.no-policy';
    } else if (this.isTrial === true) {
      this.disableSaveTooltip = undefined;
    } else if (!this.inputDataIsUpdated) {
      this.disableSaveTooltip = 'subscription.new-usage.no-changes-to-save';
    } else {
      this.disableSaveTooltip = undefined;
    }
  }

  public getCurrentSurveyResponsesPerMonthOrDefaultLimit(): number {
    return this.surveyResponsesLevelsMap[this.currentSurveyResponsesLevel] || this.defaultSurveyResponses;
  }

  public savePriceCalculation() {
    const traffic = this.trafficLevelsMap[this.currentTrafficLevel];
    if (!traffic) {
      return;
    }
    this.errorOnSavingCalculation = false;

    if (this.saveInProgress === true || this.checkoutInProgress === true) {
      return;
    }

    const surveyResponsesPerMonth = this.surveyResponsesLevelsMap[this.currentSurveyResponsesLevel];

    switch (this.provider) {
      case SubscriptionProvider.Shopify:
        // Trial and change-plan take the same path: Shopify always mints a new subscription.
        this.saveShopifyCheckout(traffic, surveyResponsesPerMonth);
        return;
      case SubscriptionProvider.Stripe:
      default:
        if (this.isTrial) {
          this.saveStripeTrial(traffic, surveyResponsesPerMonth);
        } else {
          this.saveStripeUpdate(traffic, surveyResponsesPerMonth);
        }
        return;
    }
  }

  private saveStripeTrial(traffic: number, surveyResponsesPerMonth: number) {
    this.checkoutInProgress = true;
    this.subscriptionService.setLimitationsToSessionStorage('limitations-previous', this.currentAccount.siteId, {
      tier: this.currentCustomer.tier,
      eventsPerMonth: this.initialTrafficLimit,
      accounts: this.initialAccountsLimit,
      surveyResponsesPerMonth: this.initialSurveyResponsesLimit,
    });
    this.subscriptionService.setLimitationsToSessionStorage('limitations-upgrade', this.currentAccount.siteId, {
      tier: this.tier,
      eventsPerMonth: traffic,
      accounts: this.currentAccountsLevel,
      surveyResponsesPerMonth: surveyResponsesPerMonth,
    });
    this.subscriptionService
      .createCheckoutSession(
        this.currentCustomer,
        window.location.href,
        this.getLanguage(),
        this.currentBillingFrequency,
        this.currentCurrency,
        {
          eventsPerMonth: traffic,
          accounts: this.currentAccountsLevel,
          surveyResponsesPerMonth: surveyResponsesPerMonth,
        },
        this.tier,
        this.testPrice,
      )
      .subscribe(
        (response) => {
          this.checkoutInProgress = false;
          const redirectUrl = response.url;
          if (redirectUrl) {
            window.document.location = redirectUrl;
          } else {
            this.logger.error(`Redirect URL for Stripe Customer-portal was expected but is missing`);
            throw new Error(`Redirect URL for Stripe Customer-portal was expected but is missing`);
          }
        },
        () => {
          this.snackBarService.error();
          this.checkoutInProgress = false;
        },
      );
  }

  private saveStripeUpdate(traffic: number, surveyResponsesPerMonth: number) {
    this.saveInProgress = true;
    this.subscriptionService.setLimitationsToSessionStorage('limitations', this.currentAccount.siteId, undefined);
    this.subscriptionService
      .savePriceCalculation(this.currentCustomer.id, {
        tier: this.tier,
        accounts: this.currentAccountsLevel,
        eventsPerMonth: traffic,
        surveyResponsesPerMonth: surveyResponsesPerMonth,
        billingFrequency: this.currentBillingFrequency,
        testPrice: this.testPrice,
      })
      .subscribe(
        async () => {
          this.saveInProgress = false;
          this.errorOnSavingCalculation = false;
          ApplicationStarterService.triggerEvent('onTriggerbeeClientSubscriptionUpdated', {
            customerId: this.currentCustomer.id,
            tier: this.tier,
            accounts: this.currentAccountsLevel,
            eventsPerMonth: traffic,
            surveyResponsesPerMonth: surveyResponsesPerMonth,
            billingFrequency: this.currentBillingFrequency,
          });
          const savedTier = this.tier;
          this.currentContextService.patchCurrentContext({
            limitations: {
              ...this.currentContextService.currentContext?.limitations,
              eventsPerMonth: traffic,
              accounts: this.currentAccountsLevel,
              surveyResponsesPerMonth: surveyResponsesPerMonth,
            },
            customer: {
              ...this.currentContextService.currentContext?.customer,
              tier: savedTier,
            },
          });
          this.flyoutService.closeUpgradeAccountFlyout({
            eventsPerMonth: traffic,
            accounts: this.currentAccountsLevel,
            surveyResponsesPerMonth: surveyResponsesPerMonth,
            currency: this.currentCurrency,
            price: this.calculatedPrice,
            billingFrequency: this.currentBillingFrequency,
            tier: savedTier,
          });
        },
        (error) => {
          this.errorOnSavingCalculation = true;
          this.saveInProgress = false;
          this.snackBarService.error(error.status === 402 ? 'subscription.errors.providerError' : 'generic.error');
        },
      );
  }

  private saveShopifyCheckout(traffic: number, surveyResponsesPerMonth: number) {
    this.checkoutInProgress = true;
    this.subscriptionService.setLimitationsToSessionStorage('limitations-previous', this.currentAccount.siteId, {
      tier: this.currentCustomer.tier,
      eventsPerMonth: this.initialTrafficLimit,
      accounts: this.initialAccountsLimit,
      surveyResponsesPerMonth: this.initialSurveyResponsesLimit,
    });
    this.subscriptionService.setLimitationsToSessionStorage('limitations-upgrade', this.currentAccount.siteId, {
      tier: this.tier,
      eventsPerMonth: traffic,
      accounts: this.currentAccountsLevel,
      surveyResponsesPerMonth: surveyResponsesPerMonth,
    });
    this.subscriptionService
      .createShopifyAppCharge(
        this.currentCustomer,
        window.location.href,
        this.getLanguage(),
        this.currentBillingFrequency,
        this.currentCurrency,
        {
          eventsPerMonth: traffic,
          accounts: this.currentAccountsLevel,
          surveyResponsesPerMonth: surveyResponsesPerMonth,
        },
        this.tier,
        this.testPrice,
      )
      .subscribe(
        (response) => {
          this.checkoutInProgress = false;
          const redirectUrl = response.confirmationUrl;
          if (redirectUrl) {
            window.document.location = redirectUrl;
          } else {
            this.logger.error('Confirmation URL for Shopify checkout was expected but is missing');
            this.snackBarService.error('subscription.shopify.errors.checkout-failed');
          }
        },
        () => {
          this.snackBarService.error('subscription.shopify.errors.checkout-failed');
          this.checkoutInProgress = false;
        },
      );
  }

  public switchTier(tier) {
    this.tier = tier;
    this.checkIfInputDataChanged();
    // When tier is changed we need new limitations for this tier
    this.subscriptionService.getTierLimitations(tier).subscribe((tierLimitations) => {
      this.trafficLevelsMap = this.convertToLevelsMap(tierLimitations.events);
      this.trafficLevelsCount = tierLimitations.events.length;
      this.defaultSurveyResponses = tierLimitations.defaultSurveyResponses;
      this.clampTrafficLevelToTier();
      this.recalculatePrice(); // Show new price based on new tier
    });
  }

  private clampTrafficLevelToTier(): void {
    // The last entry carries no price; it is the contact-sales placeholder.
    const highestSelectableLevel = this.trafficLevelsCount - 1;
    if (this.currentTrafficLevel <= highestSelectableLevel) {
      return;
    }
    this.currentTrafficLevel = highestSelectableLevel;
    this.showTrafficDowngradeWarning = this.trafficLevelsMap[this.currentTrafficLevel] < this.currentTraffic;
  }

  private getLanguage(): Language {
    // Check browser language
    switch (window.navigator.language) {
      case 'sv-SE':
        return Language.Swedish;
    }

    // Check current users language
    if (this.currentContextService.currentContext?.user?.language === UserLanguage.Swedish) {
      return Language.Swedish;
    }

    // By default we use English
    return Language.English;
  }

  public cancelFlyout() {
    this.flyoutService.closeUpgradeAccountFlyout(undefined);
  }

  public setMonthlyPriceFromYear(): void {
    if (this.currentBillingFrequency === SubscriptionFrequency.Year) {
      this.monthlyPriceFromYear = Math.ceil(this.calculatedPrice / 12);
    } else {
      this.monthlyPriceFromYear = null;
    }
  }

  private setDiscountedPrices(): void {
    if (!this.discount) {
      return;
    }
    this.discountedPrice = applyDiscount(this.calculatedPrice, this.discount);
    this.discountedPriceDigitsInfo = getPriceDigitsInfo(this.discountedPrice);
    this.discountedMonthlyPriceFromYear =
      this.monthlyPriceFromYear == null ? null : Math.ceil(this.discountedPrice / 12);
  }
}
